import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useFetch } from 'hooks';
import { matchPath } from 'utils';

import { usePhoneConfirmationOpen } from 'containers/common/PhoneConfirmation';

// Components
import { Mui } from 'components';

// Local components
import * as api from './api';
import PopupItem from './components/PopupItem';
import {
  getEngagementMap,
  isEngagementSuppressed,
  registerConversion,
  registerDismiss,
} from './engagementStorage';

const useStyles = Mui.makeStyles(theme => ({
  dialogPaper: {
    position: 'relative',
    overflow: 'visible',
    borderRadius: theme.shape.borderRadius,
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(-2.5),
    right: theme.spacing(-2.5),
    zIndex: theme.zIndex.modal + 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
  },
  chevronButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: theme.zIndex.modal + 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  chevronLeft: { left: theme.spacing(-2.5) },
  chevronRight: { right: theme.spacing(-2.5) },
  progressContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 6,
    zIndex: theme.zIndex.modal + 2,
    pointerEvents: 'none',
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 100ms linear',
  },
}));

function CommunicationPopups({ type, slug }) {
  const { pathname } = useLocation();
  const classes = useStyles();
  const phoneConfirmationOpen = usePhoneConfirmationOpen();

  // States
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isClosed, setIsClosed] = useState(false);
  const elapsedRef = useRef(0);
  const viewedUidsRef = useRef(new Set());

  // Fetch
  const { data, mutate } = useFetch([type && slug ? api.endpoints?.[type]?.list(slug) : null], {
    revalidateOnFocus: true,
    dedupingInterval: 30 * 1000,
    refreshInterval: 10 * 60 * 1000,
  });

  // Constants
  const activePopups = useMemo(() => {
    const rawPopups = Array.isArray(data?.results) ? data.results : [];
    const engagementMap = getEngagementMap();

    return rawPopups.filter(
      popup =>
        !popup.hasClicked &&
        !isEngagementSuppressed(engagementMap[popup.uid]) &&
        matchPath(pathname, popup?.paths || []),
    );
  }, [data?.results, pathname]);

  const safeIndex = currentIndex >= activePopups.length ? 0 : currentIndex;
  const currentPopup = activePopups[safeIndex];

  // Actions
  const handleInteraction = useCallback(
    (uid, action) => {
      if (action === 'view') viewedUidsRef.current.add(uid);
      if (action === 'click') registerConversion(uid);
      return api.sendInteraction({ type, slug, objectUid: uid, action }).then(() => mutate());
    },
    [type, slug, mutate],
  );

  const handleNext = useCallback(() => {
    elapsedRef.current = 0;
    setProgress(0);
    setCurrentIndex(prev => (prev + 1) % activePopups.length);
  }, [activePopups.length]);

  const handlePrev = useCallback(() => {
    elapsedRef.current = 0;
    setProgress(0);
    setCurrentIndex(prev => (prev - 1 + activePopups.length) % activePopups.length);
  }, [activePopups.length]);

  const handleClose = () => {
    const uid = currentPopup?.uid;
    const wasViewed = currentPopup?.hasViewed || (!!uid && viewedUidsRef.current.has(uid));
    if (uid && wasViewed) registerDismiss(uid);
    setIsClosed(true);
  };

  // Effects
  useEffect(() => {
    elapsedRef.current = 0;
    setCurrentIndex(0);
    setProgress(0);
    setIsClosed(false);
  }, [pathname]);

  useEffect(() => {
    if (activePopups.length < 2 || isClosed) {
      elapsedRef.current = 0;
      setProgress(0);
      return () => {};
    }

    if (isHovered) return () => {};

    const intervalTime = 100;
    const totalDuration = 5000;

    const timer = setInterval(() => {
      elapsedRef.current += intervalTime;

      if (elapsedRef.current >= totalDuration) {
        elapsedRef.current = 0;
        setProgress(0);
        setCurrentIndex(current => (current + 1) % activePopups.length);
      } else {
        setProgress((elapsedRef.current / totalDuration) * 100);
      }
    }, intervalTime);

    return () => clearInterval(timer);
  }, [activePopups.length, isHovered, isClosed]);

  // Render
  if (type !== 'sellers' && type !== 'institutions') return null;
  if (!slug || activePopups.length === 0 || isClosed || phoneConfirmationOpen) return null;

  return (
    <Mui.Dialog
      open
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}>
      <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <Mui.IconButton onClick={handleClose} className={classes.closeButton}>
          <Mui.Icon>close</Mui.Icon>
        </Mui.IconButton>
        {activePopups.length > 1 && (
          <>
            <Mui.IconButton
              onClick={handlePrev}
              className={`${classes.chevronButton} ${classes.chevronLeft}`}>
              <Mui.Icon>chevron_left</Mui.Icon>
            </Mui.IconButton>
            <Mui.IconButton
              onClick={handleNext}
              className={`${classes.chevronButton} ${classes.chevronRight}`}>
              <Mui.Icon>chevron_right</Mui.Icon>
            </Mui.IconButton>
          </>
        )}
        <PopupItem
          key={currentPopup.uid}
          type={type}
          slug={slug}
          popup={currentPopup}
          onInteraction={handleInteraction}
          totalPopups={activePopups.length}
          currentIndex={safeIndex}
        />
        {activePopups.length > 1 && (
          <div className={classes.progressContainer}>
            <div className={classes.progressBar} style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </Mui.Dialog>
  );
}

CommunicationPopups.propTypes = {
  type: PropTypes.oneOf(['sellers', 'institutions']).isRequired,
  slug: PropTypes.string,
};

export default CommunicationPopups;
